<template lang="pug">
  .product-preview(
    ref="preview"
    :class="[ `component-${component}`, { 'show-actions': showActions, 'has-no-properties': !visibleProperties.length, 'has-no-media': !media.length } ]"
    @mouseover.prevent.stop="focus('main')"
    @mouseleave.prevent.stop="leave"
  )
    .arrow-container
      .arrow
    .product-description
      .top
        .left
          h3.title {{ name }}
          h3.nr {{ nr }}
        .right.price
          nobr {{ price }}
      //- .description(v-if="description" v-html="description")
    .product-info(:id="`product-info-${uniqid}`")
      .media(v-if="media.length")
        .main-media(v-if="mainMedia && mobile")
          FullVideo(
            v-if="mainMedia.type === 'video'"
            :key="`main-${mainMedia.id}`"
            :media="mainMedia"
            :product="product"
            :play="active"
          )
          FullImage(
            v-else-if="mainMedia.type === 'image'"
            :key="`main-${mainMedia.id}`"
            :media="mainMedia"
            :product="product"
            :width="windowWidth"
          )
        .thumbnails(v-if="thumbnails.length")
          template(v-for="el in thumbnails")
            PreviewVideo(
              v-if="el.type === 'video'"
              :key="el.id"
              :media="el"
              :product="product"
              :aspect-ratio="1 / 1"
              @maximize="maximize"
            )
            PreviewImage(
              v-else-if="el.type === 'image'"
              :key="el.id"
              :media="el"
              :product="product"
              :aspect-ratio="1 / 1"
              @maximize="maximize"
            )
      .properties(:id="`product-properties-${uniqid}`")
        .invalid.mb-3(v-if="!valid")
          .alert.warning Dieses Produkt ist aus technischen Gründen nicht verfügbar
        .out-of-stock.mb-3(v-if="product.manage_stock && !product.qty_sum")
          .alert.warning Dieses Produkt ist momentan nicht verfügbar
        .description(v-if="description" v-html="description")
        Matrix(
          v-if="component === 'matrix'"
          :valid="valid"
          :article-properties="configurableArticleProperties"
          :product-properties="configurableProductProperties"
          :infos="nonConfigurableProperties"
          :articles="product.articles"
          :valid-articles="validArticles"
          :selected-options="selectedOptions"
          :manage-stock="product.manage_stock"
          @select-property="selectPropertyOption"
        )
        .preorder-infos(v-else-if="component === 'preorder'")
          .rows
            Property(
              v-for="property in nonConfigurableProperties"
              :key="`${product.id}-${property.id}`"
              :property="property"
            )
        .rows(v-else)
          Property(
            v-for="property in visibleProperties"
            :valid="valid"
            :key="`${product.id}-${property.id}`"
            :property="property"
            :active-option="selectedOptions[property.id]"
            :sellable-articles="sellableArticles"
            :valid-articles="validArticles"
            :manage-stock="product.manage_stock"
            @select="selectPropertyOption"
          )
      .actions(v-if="component !== 'preorder'")
        .left.mobile.price
          nobr {{ price }}
        .buttons(:class="{ active: validPropertySelection && validArticles && validArticles.length === 1 }")
          button.button.is-large(v-if="valid" @click="addToCart") In den Warenkorb
          button.wish-list.button.is-link.has-icon.block.w-full
            font-awesome-icon(:icon="{ prefix: 'far', iconName: 'heart' }")
            | Auf den Wunschzettel
    Preorder(
      v-if="component === 'preorder'"
      :valid="valid"
      :article-properties="configurableArticleProperties"
      :product-properties="configurableProductProperties"
      :articles="product.articles"
      :valid-articles="validArticles"
      :selected-options="selectedOptions"
      :manage-stock="product.manage_stock"
      @select-property="selectPropertyOption"
    )
</template>

<script>
import uniqid from 'uniqid';
import { mapGetters, mapMutations, mapActions } from 'vuex';

import { get, orderBy, debounce, trim, cloneDeep } from '@/helpers';
import { moneyFormat } from '@/filters';

import PreviewImage from '@/extensions/shop/components/ProductList/Partials/PreviewImage.vue';
import PreviewVideo from '@/extensions/shop/components/ProductList/Partials/PreviewVideo.vue';
import FullImage from '@/extensions/shop/components/ProductList/Partials/FullImage.vue';
import FullVideo from '@/extensions/shop/components/ProductList/Partials/FullVideo.vue';
import Property from '@/extensions/shop/components/ProductList/Partials/Property.vue';
import Matrix from '@/extensions/shop/components/ProductList/Partials/Matrix.vue';
import Preorder from '@/extensions/shop/components/ProductList/Partials/Preorder.vue';

import store from '@/store';

export default {
    name: 'ProductPreview',
    data: () => ({
        uniqid: uniqid(),
        windowWidth: undefined,
        selectedOptions: {} // key is property id
    }),
    props: {
        product: {
            type: Object,
            required: true
        },
        active: {
            type: Boolean,
            default: false
        },
        display: {
            type: String,
            default: 'rows'
        }
    },
    components: {
        // Article,
        PreviewImage,
        PreviewVideo,
        FullImage,
        FullVideo,
        Property,
        Matrix,
        Preorder
    },
    computed: {
        nr() {
            return this.product.nr;
        },
        name() {
            let name = this.product.name;

            if (this.validArticles && this.validArticles.length && this.validArticles[0].name) {
                name = this.validArticles[0].name;
            }

            return name;
        },
        description() {
            let description = get(this.product, 'description');
            // if (!description) description = this.productProperties.find(el => el.key === 'buchinfo');
            // if (!description) description = '';
            // if (typeof description == 'object') return description.value;

            if (this.validArticles && this.validArticles.length && this.validArticles[0].description) {
                description = this.validArticles[0].description;
            }

            return description;
        },
        media() {
            let media = this.product.media;

            if (this.validArticles && this.validArticles.length && this.validArticles[0].media) {
                media = this.validArticles[0].media;
            }

            return media;
        },
        valid() {
            // check if this product is valid
            // by required stuff is missing
            // - price
            // ...
            return !!this.product.price;
        },
        component() {
            let component = 'component';
            if (
                this.configurableArticleProperties.length === 2 &&
                this.configurableProductProperties.length === 0 &&
                this.display === 'preorder'
            ) {
                component = 'preorder';
            } else if (this.configurableArticleProperties.length === 2 && this.display === 'matrix') {
                component = 'matrix';
            }
            return component;
        },
        properties() {
            let properties = [];

            const normalizePropertyOptions = el => {
                const propType = el.property.propType;
                let options = el.values.map(el => {
                    let key = propType.value_field;
                    let value = get(el, key);
                    if (!value) {
                        key = propType.value_field_alt;
                        value = get(el, key);
                    }

                    return {
                        id: el.id,
                        image: el.image,
                        value,
                        key
                    };
                });
                return options;
            };

            if (this.product.propertyPivots && this.product.propertyPivots.length) {
                this.product.propertyPivots.forEach(el => {
                    const id = el.property.id;
                    const label = el.property.name;
                    const visible = el.property.visible_on_product_listing;

                    // const propType = el.property.propType;
                    const articleRequired = el.property.article_required;

                    // let key = el.key
                    const options = normalizePropertyOptions(el);
                    let property = {
                        id,
                        label,
                        options,
                        articleRequired,
                        visible
                    };

                    // if (el.key.toLowerCase() === 'länge') {
                    //   property.label = 'length';
                    // }
                    properties.push(property);
                });
            }

            return properties;
        },
        visibleProperties() {
            return this.properties.filter(property => {
                return (
                    property.visible !== false || property.options.length > 1
                    // || (property.options.length === 1 && property.options[0].value !== this.description)
                );
            });
        },
        configurableArticleProperties() {
            return this.visibleProperties.filter(property => {
                return property.options && property.options.length > 1 && property.articleRequired;
            });
        },
        configurableProductProperties() {
            return this.visibleProperties.filter(property => {
                return property.options && property.options.length > 1 && property.articleRequired === false;
            });
        },
        nonConfigurableProperties() {
            return this.visibleProperties.filter(property => property.options && property.options.length <= 1);
        },
        validPropertySelection() {
            let valid = false;
            const pendingProperties = this.visibleProperties.filter(property => {
                return property.options.length > 1 && !this.selectedOptions[property.id];
            });
            if (!pendingProperties.length) valid = true;
            return valid;
        },
        sellableArticles() {
            let articles = this.product.articles;

            // pay attention to stock
            const manageStock = this.product.manage_stock;
            if (manageStock && this.product.articles && this.product.articles.length) {
                articles = this.product.articles.filter(article => article.qty_sum > 0);
            }
            return articles;
        },
        validArticles() {
            let articles = this.sellableArticles;
            if (this.sellableArticles && this.sellableArticles.length) {
                articles = this.sellableArticles.filter(article => {
                    let match = true;

                    // check qty in cart
                    // qty in cart must be smaller
                    // than article.qty_sum (if manageStock)
                    if (this.product.manage_stock) {
                        let cartQty = 0;
                        this.cart.articlePivots.forEach(articlePivot => {
                            if (articlePivot.article.id === article.id) {
                                cartQty += articlePivot.qty;
                            }
                        });
                        if (article.qty_sum <= cartQty) match = false;
                    }

                    // all property pivots must match
                    if (match) {
                        article.propertyPivots.forEach(el => {
                            const property = el.property;
                            const value = el.value;
                            if (this.selectedOptions[property.id]) {
                                const option = this.selectedOptions[property.id];
                                if (value[option.key] !== option.value) match = false;
                            }
                        });
                    }
                    return match;
                });
            }
            return articles;
        },
        showActions() {
            if (this.component === 'preorder') return false;

            let show = this.currentProduct && this.product.id === this.currentProduct.id;
            const breakpoint = parseInt(this.tailwind.theme.screens.md);
            if (this.windowWidth < breakpoint) show = true;
            return show;
        },
        mobile() {
            const breakpoint = parseInt(this.tailwind.theme.screens.md);
            return this.windowWidth < breakpoint;
        },

        mainMedia() {
            let media;

            // get the "main" media object (is marked for list view)
            // media = this.media.find(el => get(el, 'settings.show.list', true) === true);

            // custom for Rundholz:
            // get the "main" media object (is video)
            media =
                this.media.find(el => {
                    return get(el, 'settings.show.list', true) === true && get(el, 'type') === 'video';
                }) ||
                this.media.find(el => {
                    return get(el, 'settings.show.detail', true) === true && get(el, 'type') === 'video';
                }) ||
                this.media.find(el => {
                    return get(el, 'settings.show.list', true) === true;
                });

            return media;
        },
        remainingMedia() {
            let media = [];
            // get the "remaining" media collection (marked for detail)
            // let others = this.media.filter(el => {
            //   return get(el, 'settings.show.detail', true) === true && get(main, 'id') !== el.id;
            // });

            // custom for Rundholz:
            // get the remaining" media collection (marked for list or detail)
            media = this.media.filter(el => {
                return (
                    (get(el, 'settings.show.list', true) === true || get(el, 'settings.show.detail', true) === true) &&
                    get(this.mainMedia, 'id') !== el.id
                );
            });

            return orderBy(media, 'position');
        },
        allMedia() {
            let media = this.remainingMedia;
            if (this.mainMedia) {
                media = [this.mainMedia, ...this.remainingMedia];
            }
            return media;
        },
        thumbnails() {
            let media = [];

            const breakpoint = parseInt(this.tailwind.theme.screens.md);

            if (this.mainMedia && !this.widescreen && this.windowWidth >= breakpoint) {
                media = [this.mainMedia, ...this.remainingMedia];
            } else if (this.mainMedia && this.remainingMedia.length === 0 && this.windowWidth >= breakpoint) {
                media = [this.mainMedia];
            } else {
                media = this.remainingMedia;
            }

            return media;
        },
        price() {
            if (!this.product.price) return null;

            const code =
                get(this.customer, 'customerGroup.currency.iso4217') ||
                get(this.defaultCustomerGroup, 'customerGroup.currency.iso4217') ||
                'EUR';

            // return moneyFormat(price.value - tax, code);
            let string = moneyFormat(this.product.price.value, code);

            // get all prices from related articles
            // define range

            let range = {
                from: this.product.price.value,
                to: this.product.price.value
            };

            if (this.validArticles && this.validArticles.length) {
                this.validArticles.forEach(article => {
                    if (article.price) {
                        range.from = Math.min(range.from, article.price.value);
                        range.to = Math.max(range.from, article.price.value);
                    }
                });

                if (range.from < range.to) {
                    string = 'ab ' + moneyFormat(range.from, code);
                    // string = moneyFormat(range.from, code) + ' - ' + moneyFormat(range.to, code);
                }
            }

            return string;
        },
        currentProduct() {
            if (this.focusedMedia && this.focusedMedia.product) return this.focusedMedia.product;
            return this.activeProduct;
        },
        ...mapGetters({
            customer: 'shop/customer',
            defaultCustomerGroup: 'shop/defaultCustomerGroup',
            defaultTax: 'shop/defaultTax',
            activeProduct: 'shop/activeProduct',
            focusedMedia: 'shop/focusedMedia',
            cart: 'shop/cart',
            tailwind: 'tailwind',
            widescreen: 'shop/widescreen'
        })
    },
    methods: {
        focus(media) {
            if (media === 'main') {
                if (!this.mainMedia) media = null;
                else media = this.mainMedia;
            }
            this.setFocusedMedia({ product: this.product, media });
        },
        leave() {
            this.removeFocusedMedia();
        },
        calculateVisiblity() {
            const isInViewport = element => {
                if (!element) return false;

                let height = window.innerHeight;

                // override element for mobile
                const breakpoint = parseInt(this.tailwind.theme.screens.md);
                if (window.innerWidth < breakpoint) {
                    const figure = element.querySelector('.main-media figure');
                    if (figure) element = figure;
                } else {
                    height -= 50; // substr footer height
                }

                const style = getComputedStyle(element);
                const rect = element.getBoundingClientRect();

                // const top = rect.top + window.scrollY;
                const top = rect.top + parseFloat(style.paddingTop);
                const bottom = rect.bottom + parseFloat(style.paddingBottom);

                // default visible| if the element is fully in viewport
                //                | or too large for the viewport
                let visible = (bottom < height && top > 0) || (bottom > height && top < 0);

                // mobile visible| if .main-media > figure is at least for 66% in viewport
                //               | or too large for the viewport
                if (window.innerWidth < breakpoint) {
                    visible =
                        (bottom < height + element.clientHeight * 0.33 && top > element.clientHeight * -0.33) ||
                        (bottom > height && top < 0);
                } else if (height < 500 || element.clientHeight > height / 2) {
                    visible =
                        (top > -100 && bottom < height + element.clientHeight / 2) || (bottom > height && top < 0);
                }

                // if (this.product.nr === '2345760896') {
                //   console.clear();
                //   console.log('top:', top);
                //   console.log('bottom:', bottom);
                //   console.log('height:', height);
                //   console.log('client height:', element.clientHeight);
                //   console.log('===================');
                //   console.log('visible: ', visible);
                //   console.log('===================');
                // }

                return visible;
            };

            if (isInViewport(this.$refs.preview)) {
                this.addVisibleProduct(this.product);
                // load articles
                if (!this.product.articles && !(this.product.manage_stock && !this.product.qty_sum)) {
                    this.fetchProductArticles(this.product.id).then(articles => {
                        this.$emit('got-articles', articles);
                    });
                }
            } else {
                this.removeVisibleProduct(this.product);
            }
        },
        maximize({ event, media }) {
            let boxes = [];

            let key;
            this.allMedia.forEach((el, i) => {
                if (el.id === media.id) key = i;

                if (el.type === 'video') {
                    const storageRoute = trim(
                        `${process.env.VUE_APP_API_HTTP}${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PUBLIC_STORAGE_ROUTE}`,
                        '/'
                    );
                    const cdnEndpoint = trim(
                        `${process.env.VUE_APP_CDN_HTTP}${process.env.VUE_APP_CDN_ENDPOINT}${process.env
                            .VUE_APP_CDN_ROOT || ''}`,
                        '/'
                    );

                    let mime = get(el, 'video.source.mime');
                    let src = `${storageRoute}/videos/${el.video.id}/${el.video.source.name}`;

                    if (get(el, 'video.hls')) {
                        mime = get(el, 'video.hls.mime');
                        src = `${cdnEndpoint}/${el.video.hls.path}/${el.video.hls.name}`;
                    }

                    boxes.push({
                        id: el.id,
                        // src: 'https://local.cdn.cityoffer.online/artem/videos/product-3259/chillman/playlist.m3u8',
                        src,
                        type: 'video',
                        mime,
                        width: el.video.width,
                        height: el.video.height
                        // title: 'test'
                    });
                } else if (el.type === 'image') {
                    let root = '';
                    const endpoint = `${process.env.VUE_APP_API_HTTP}${process.env.VUE_APP_API_ENDPOINT}`;
                    if (process.env.VUE_APP_API_PUBLIC_STORAGE_ROUTE)
                        root = process.env.VUE_APP_API_PUBLIC_STORAGE_ROUTE;
                    const base = endpoint + root + 'images/';

                    boxes.push({
                        id: el.id,
                        src: `${base}${el.image.id}/${el.image.name}`,
                        msrc: `${base}${el.image.id}/${el.image.name}?fit&h=50`,
                        type: el.type,
                        mime: get(el, 'image.source.mime'),
                        w: el.image.width,
                        h: el.image.height
                        // title: 'test'
                    });
                }
            });

            this.$photoswipe.open(event, key, boxes);
        },
        selectPropertyOption({ property, option }) {
            if (this.selectedOptions[property.id] && this.selectedOptions[property.id].id === option.id) {
                this.$delete(this.selectedOptions, property.id);
            } else {
                const value = { label: property.label, articleRequired: property.articleRequired, ...option };
                this.$set(this.selectedOptions, property.id, value);

                // check if there are validArticles
                // if not delete all selectedOptions
                // that are not available in combination
                // with the latest selection

                if (this.sellableArticles.length) {
                    let attempts = 0;
                    while (!this.validArticles.length && attempts < 1) {
                        ++attempts;

                        let match = false;
                        if (!match) {
                            this.properties.forEach(prop => {
                                if (
                                    prop.articleRequired &&
                                    prop.options.length &&
                                    this.selectedOptions[prop.id] &&
                                    prop.id !== property.id
                                ) {
                                    // find sellable article with selected options
                                    // without the current opt
                                    // if there are some, delete this prop from selectedOptions

                                    const articles = this.sellableArticles.filter(article => {
                                        // all property pivots must match
                                        let match = true;
                                        article.propertyPivots.forEach(el => {
                                            const property = el.property;
                                            const value = el.value;
                                            if (prop.id !== property.id && this.selectedOptions[property.id]) {
                                                const option = this.selectedOptions[property.id];
                                                if (value[option.key] !== option.value) match = false;
                                            }
                                        });
                                        return match;
                                    });

                                    if (articles.length) {
                                        this.$delete(this.selectedOptions, prop.id);
                                        match = true;
                                    }
                                }
                            });
                        }
                    }
                }
            }
        },
        addToCart() {
            if (!this.validArticles || this.validArticles.length > 1) {
                const errorMessage = {
                    id: uniqid(),
                    title: 'Fehler',
                    text:
                        'Es gibt ein technisches Problem.<br>Bitte versuchen Sie es zu einem späteren Zeitpunkt erneut.',
                    type: 'error',
                    autoclose: 8000
                };
                this.addMessage(errorMessage);
            } else {
                let article = this.validArticles[0];
                article.product = cloneDeep(this.product);
                delete article.product.articles;

                let productPropertyValues = [];
                Object.keys(this.selectedOptions).map(key => {
                    if (!this.selectedOptions[key].articleRequired) {
                        productPropertyValues.push(this.selectedOptions[key].id);
                    }
                });

                this.addArticleToCart({ article, productPropertyValues });

                const properties = Object.keys(this.selectedOptions)
                    .map(key => `${this.selectedOptions[key].label}: ${this.selectedOptions[key].value}`)
                    .join('<br>');

                const text = `
          <p>Der folgende Artikel wurde Ihrem Warenkorb hinzugefügt</p>
          <blockquote class="mt-2 pl-3 border-l-2 border-white border-opacity-50">
            <h4 class="text-xl leading-none mb-1">${this.name}</h4>
            <p class="text-xs mb-2 text-white text-opacity-75">#${this.nr}</p>
            <p>${properties}</p>
            <p>Preis: ${this.price}</p>
          </blockquote>
        `;

                const message = {
                    id: uniqid(),
                    text,
                    type: 'success',
                    autoclose: 4000
                };
                this.addMessage(message);
                this.selectedOptions = {};
            }
        },
        ...mapMutations({
            addVisibleProduct: 'shop/addVisibleProduct',
            removeVisibleProduct: 'shop/removeVisibleProduct',
            setFocusedMedia: 'shop/setFocusedMedia',
            removeFocusedMedia: 'shop/removeFocusedMedia'
        }),
        ...mapActions({
            addMessage: 'message/add',
            addArticleToCart: 'shop/addArticleToCart',
            // removeArticleFromCart: 'shop/removeArticleFromCart',
            fetchProductArticles: 'shop/fetchProductArticles'
        })
    },
    beforeMount() {
        const updateWindowWidth = () => {
            this.windowWidth = document.body.clientWidth;
        };
        window.addEventListener('resize', debounce(updateWindowWidth, 300));
        updateWindowWidth();
    },
    mounted() {
        // window.addEventListener('scroll', debounce(this.calculateVisiblity, 150));
        window.addEventListener('scroll', this.calculateVisiblity);
        setTimeout(this.calculateVisiblity, 100);
    },
    beforeDestroy() {
        store.commit('shop/clearVisibleProducts');
        store.commit('shop/removeFocusedMedia');

        window.removeEventListener('scroll', this.calculateVisiblity);
    }
};
</script>

<style lang="sass" scoped>
.product-preview
  position: relative
  .product-description
    margin-bottom: 1rem
    .top
      display: flex
      width: 100%
      justify-content: space-between
      align-items: center
      // align-items: flex-end
      gap: 1rem
    .price
      font-size: 1.6rem
      display: none
      @screen md
        display: initial
  .product-info
    @apply grid
    width: 100%
    grid-gap: 1rem
    grid-template-rows: auto
    grid-template-columns: auto
    @screen md
      grid-template-columns: max-content auto
    .actions
      display: none
      .buttons
        button + button
          margin-top: 4px
    .media
      .thumbnails
        display: flex
        flex-wrap: wrap
        justify-content: flex-start
        gap: .6rem
        @screen md
          justify-content: flex-start
          max-width: calc(1 * 240px + .4rem)
        ::v-deep .preview-image
          @screen md
            figure
              max-width: 55px
              max-height: calc(55px / (1 / 1))
            &:nth-child(-n+1) figure
              max-width: initial
              max-height: initial
      .main-media
        margin-bottom: .5rem
        ::v-deep
          figure
            margin-left: -1rem
            width: calc(100% + 2rem)
        @screen md
          display: none
  &.has-no-properties
    .properties
      display: none
  // &.has-no-properties
  //   .product-info
  //     grid-template-rows: auto
  //     @screen sm
  //       grid-template-columns: minmax(0, max-content) auto
  &.has-no-media
    .product-description
      margin-bottom: .2rem
    .product-info
      grid-template-columns: auto

  &.show-actions
    .product-info
      @screen md
        grid-template-rows: repeat(2, auto)
        grid-template-columns: max-content auto
      @screen lg
        grid-template-rows: auto
        grid-template-columns: max-content auto max-content
      .actions
        @apply flex flex-col items-end justify-between
        @screen md
          @apply flex-col items-end justify-start
          grid-column: 2
        @screen lg
          grid-column: 3
        .price
          font-size: 1.5rem
          line-height: 1.8rem
          height: 2rem
          margin-bottom: 1rem
          @screen md
            display: none
        .buttons
          opacity: .3
          pointer-events: none
          transition: opacity .15s ease
          &.active
            pointer-events: initial
            opacity: 1
    &.has-no-media
      .product-info
        grid-template-columns: auto
        .actions
          grid-column: 1
        @screen sm
          grid-template-columns: minmax(min-content, 100%) max-content
          .actions
            grid-column: 2
    // &.has-no-properties
    //   .actions
    //     @apply items-center
    //     @screen sm
    //       @apply items-start
    //       grid-row: 1
    //       grid-column: 2

  h3
    @apply text-xl
  // .title
  //   @apply text-2xl
  // .nr
  //   @apply text-xs

  .product-description .description
    @apply text-sm mt-2
  .properties .description
    @apply mb-2
    max-width: 580px

  .properties
    &::v-deep
      .property
        display: contents
        &.selectable
          .label
            @apply items-start
            padding-top: 0.275em // fix text position
        .label, .value
          @apply flex items-center
        .label
          max-width: 120px
      .is-out-of-stock
        position: relative
        &:before,
        &:after
          position: absolute
          content: ''
          width: 100%
          height: 100%
          left: 0
          right: 0
          background-color: transparent
          background: linear-gradient(to top right, #0000 calc(50% - 1px), #000, #0000 calc(50% + 1px))
          opacity: .5
        &:after
          background: linear-gradient(to bottom right, #0000 calc(50% - 1px), #000, #0000 calc(50% + 1px))
    .rows
      display: grid
      grid-column-gap: 1rem
      grid-template-rows: auto
      grid-template-columns: minmax(0, max-content) auto

.preorder-infos .rows::v-deep .value
  min-height: initial

.preorder
  @apply mt-4

.arrow-container
  opacity: 0
  display: none
  position: absolute
  right: -35px
  justify-content: flex-end
  padding-top: 20px
  @screen widescreen
    @screen lg
      display: flex
      // right: -6rem
      right: -35px
  .arrow
    width: 0
    height: 0
    border-top: 76px solid transparent
    border-bottom: 76px solid transparent
    border-left: 25px solid var(--color-primary)
</style>
